// ─── messageAdminApi ─────────────────────────────────────────────────────────
// Backs the "Message the Project Administrator" dashboard widget.
//
//   POST /api/dashboard/message-admin/   {project, message, reply_to}
//
// The server resolves the recipients from the project's admin list — the
// caller never supplies a destination address — and puts `reply_to` in the
// Reply-To header so an admin can answer the sender directly.
//
// URL + token conventions follow widgetConfigStore.js so this works both
// same-origin on staging and against a split API host in local dev. The
// Authorization header is sent when a token exists but is not required:
// public-portal visitors post anonymously, and the backend gates that on
// the project having its Public Portal enabled.

import axios from 'axios'

const API_URL         = import.meta.env.VITE_API_URL         || ''
const API_BASE_PATH   = import.meta.env.VITE_API_BASE_PATH   || '/api'
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'

const getToken = () =>
  localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY) || ''

// Mirrors the server-side cap so an over-long message is caught before it
// costs a round-trip. Keep in sync with MAX_MESSAGE_CHARS in
// app_dashboard/message_admin_views.py.
export const MAX_MESSAGE_CHARS = 5000

/**
 * Send a message to the project's administrators.
 *
 * Resolves to the parsed response ({success, recipients}) or throws an
 * Error whose `message` is safe to show the user — the backend returns a
 * human-readable `error` string for every rejection (validation, 401/403,
 * 429 rate limit, 502 mail failure), so we surface that verbatim rather
 * than inventing our own copy per status code.
 */
export async function sendMessageToAdmin({ projectId, message, replyTo }) {
  const token = getToken()
  try {
    const res = await axios.post(
      `${API_URL}${API_BASE_PATH}/dashboard/message-admin/`,
      // The recipient is the project's configured "Forward Message To" address
      // (read server-side), falling back to the project admins.
      { project: projectId, message, reply_to: replyTo },
      {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    )
    return res.data
  } catch (err) {
    const serverMessage = err?.response?.data?.error
    throw new Error(
      serverMessage ||
      'Could not send your message right now. Please try again later.',
    )
  }
}
